/**
 * CityPicker (Iter346) — single-select combobox with lead counts.
 *
 * Loads city → lead-count buckets from the backend and merges them with
 * a curated fallback list (e.g. major Florida cities). Cities WITH
 * leads bubble to the top, sorted by count desc; cities without leads
 * show below. Users can free-type any string not in the list.
 *
 * Props:
 *   value           — current city string
 *   onChange(next)  — setter
 *   fetchUrl        — endpoint returning `{cities:[{city, count}]}`
 *   fallbackCities  — array of strings shown when the backend returns 0
 *   authHeader      — passed through to axios
 *   testidPrefix
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, MapPin, Search, X } from "lucide-react";

export default function CityPicker({
  value,
  onChange,
  fetchUrl,
  fallbackCities = [],
  authHeader,
  placeholder = "Digite ou selecione uma cidade",
  testidPrefix = "city-picker",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]); // [{city, count}]
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  // Fetch once when opened for the first time.
  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    if (loadedRef.current || !fetchUrl) return;
    loadedRef.current = true;
    setLoading(true);
    try {
      const r = await axios.get(fetchUrl, { ...authHeader, timeout: 12000 });
      setRows(r.data?.cities || []);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [fetchUrl, authHeader]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Merge backend + fallback into a single list. Cities with a count
  // come first (desc); zero-count cities are appended alphabetically.
  const merged = useMemo(() => {
    const map = new Map();
    for (const r of rows) map.set(r.city.toLowerCase(), { city: r.city, count: r.count });
    for (const c of fallbackCities) {
      const key = c.toLowerCase();
      if (!map.has(key)) map.set(key, { city: c, count: 0 });
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.city.localeCompare(b.city);
    });
    return arr;
  }, [rows, fallbackCities]);

  const filtered = useMemo(() => {
    if (!q.trim()) return merged;
    const needle = q.toLowerCase();
    return merged.filter((r) => r.city.toLowerCase().includes(needle));
  }, [merged, q]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const totalWithLeads = merged.filter((r) => r.count > 0).length;

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid={`${testidPrefix}-wrapper`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testidPrefix}-toggle`}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full h-9 px-3 rounded-lg border border-white/15 bg-black/30 text-[13px] text-white flex items-center gap-2 hover:border-white/30"
      >
        <MapPin className="w-3.5 h-3.5 opacity-60 shrink-0" />
        <span className={`flex-1 text-left truncate ${value ? "" : "opacity-50"}`}>
          {value || placeholder}
        </span>
        {value && (
          <button type="button" tabIndex={-1}
            data-testid={`${testidPrefix}-clear`}
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            title="Limpar"
            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-white/10">
            <X className="w-2.5 h-2.5" />
          </button>
        )}
        <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`${testidPrefix}-menu`}
          className="absolute left-0 right-0 mt-1 z-50 rounded-lg border shadow-xl overflow-hidden"
          style={{ background: "rgba(11,17,32,0.98)", borderColor: "rgba(255,255,255,0.15)" }}
          role="listbox"
        >
          <div className="p-2 border-b border-white/10 flex items-center gap-2">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && q.trim()) {
                  onChange(q.trim());
                  setOpen(false);
                }
                if (e.key === "Escape") setOpen(false);
              }}
              data-testid={`${testidPrefix}-search`}
              placeholder="Filtrar cidades ou digitar nova…"
              className="flex-1 h-7 bg-transparent text-[12.5px] text-white outline-none placeholder:text-slate-500" />
          </div>

          <div className="text-[10px] uppercase tracking-widest text-slate-500 px-3 pt-2 pb-1 flex items-center justify-between">
            <span>{filtered.length} cidade{filtered.length === 1 ? "" : "s"}</span>
            {totalWithLeads > 0 && (
              <span className="text-emerald-400/80">
                {totalWithLeads} com leads
              </span>
            )}
          </div>

          <div className="max-h-64 overflow-y-auto py-1"
               data-testid={`${testidPrefix}-list`}>
            {loading && (
              <div className="px-3 py-3 text-[11.5px] text-slate-400">
                Carregando cidades…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <button type="button"
                data-testid={`${testidPrefix}-use-typed`}
                onClick={() => { onChange(q.trim()); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-[12.5px] text-emerald-300 hover:bg-white/5">
                Usar "{q.trim()}" mesmo assim →
              </button>
            )}
            {!loading && filtered.map((row) => {
              const active = value === row.city;
              return (
                <button type="button"
                  key={row.city}
                  onClick={() => { onChange(row.city); setOpen(false); setQ(""); }}
                  data-testid={`${testidPrefix}-opt-${row.city.replace(/\W+/g, "_")}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[12.5px] text-left ${
                    active ? "bg-emerald-500/15 text-emerald-100" : "text-slate-200 hover:bg-white/5"}`}
                >
                  <span className="truncate">{row.city}</span>
                  {row.count > 0
                    ? (
                      <span className="text-[10.5px] font-bold tabular-nums px-1.5 h-4 rounded-full inline-flex items-center bg-emerald-500/20 text-emerald-200 shrink-0"
                            title={`${row.count} lead${row.count === 1 ? "" : "s"} indexado${row.count === 1 ? "" : "s"}`}>
                        {row.count}
                      </span>
                    )
                    : (
                      <span className="text-[10px] text-slate-500 shrink-0">—</span>
                    )
                  }
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
